// Getting the three sources IN.
//
// A schedule is a file, a layout is a drawing somebody traces, and the
// model is a commit already on the server - three genuinely different
// acts, so three tabs rather than one "upload" that means something
// different depending on what you picked.
//
// What they have in common is the end: each produces `observations` and
// posts them to POST /sources, which queues the reconciliation. Nothing
// here decides anything about the board - that is the review screen's
// job, and this screen says so.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { popupInStage } from "./stagePopup";
import { usePreference } from "helpers/userPrefs";
import { restoreTicks, missingTickNote } from "./sourceTicks";
import {
  Tabs, Upload, Button, Table, Select, Alert, Steps, message, Tag, Input,
  Popconfirm, Empty, Spin, Space, Tooltip,
} from "antd";
import {
  InboxOutlined, DeleteOutlined, FileExcelOutlined, HighlightOutlined,
  BuildOutlined, UndoOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import moment from "moment";
import { url, tokenRef } from "api";
import { renderPdfThumbnail } from "helpers/pdfPreview";
import { addSource, deleteSource, getSources } from "api/tracking/fieldTrackingApi";
import SheetStage, { pointAt as stagePointAt } from "./SheetStage";
import { buildVersion } from "helpers/buildVersion";
import { systemColor, systemLabel } from "./trackingTheme";
import { sheetKind, sheetSelectProps } from "./sheetOptions";

const MAX_ROWS = 5000;

// What a take-off column can mean. `length` is the one that matters most
// and the one most often missing: without it the board falls back to
// counting segments and says so, rather than pretending to measure.
const FIELDS = [
  { key: "mark", label: "Mark / tag", hint: "CT-204" },
  { key: "system", label: "System", hint: "Cable tray, conduit…" },
  { key: "size", label: "Size", hint: '6", 800A' },
  { key: "length", label: "Length", hint: "feet — drives every percentage" },
  { key: "grid", label: "Grid", hint: "C-4" },
  { key: "level", label: "Level", hint: "L2" },
  { key: "name", label: "Description", hint: "free text" },
  { key: "count", label: "Count", hint: "how many" },
];

// Header detection: the first row in which at least two cells look like
// one of our field names. A take-off routinely opens with a title block
// and a blank line or two, so assuming row 1 is the header is wrong more
// often than it is right.
const HEADER_HINTS = /mark|tag|system|type|size|dia|length|qty|count|grid|level|floor|desc/i;
function detectHeaderRow(rows) {
  for (let i = 0; i < Math.min(rows.length, 15); i += 1) {
    const hits = (rows[i] || []).filter((c) => HEADER_HINTS.test(String(c || ""))).length;
    if (hits >= 2) return i;
  }
  return 0;
}
// A first guess at the mapping, so the common case is a glance and a
// click rather than eight dropdowns.
function guessMapping(header) {
  const out = {};
  header.forEach((h, i) => {
    const t = String(h || "").toLowerCase();
    if (!t) return;
    if (!out.mark && /(^|\b)(mark|tag|id|item)\b/.test(t)) out.mark = i;
    else if (!out.system && /(system|type|service|discipline)/.test(t)) out.system = i;
    else if (!out.size && /(size|dia|width|trade size)/.test(t)) out.size = i;
    else if (!out.length && /(length|len|ft|linear)/.test(t)) out.length = i;
    else if (!out.grid && /(grid|location|zone)/.test(t)) out.grid = i;
    else if (!out.level && /(level|floor|elev)/.test(t)) out.level = i;
    else if (!out.count && /(count|qty|quantity)/.test(t)) out.count = i;
    else if (!out.name && /(desc|name|remark)/.test(t)) out.name = i;
  });
  return out;
}

async function readTabular(file) {
  const name = String(file.name || "").toLowerCase();
  if (name.endsWith(".csv")) {
    const text = await file.text();
    const parsed = Papa.parse(text, { skipEmptyLines: true });
    return parsed.data || [];
  }
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
}

// ---- 1. schedule -------------------------------------------------------
// A DISABLED BUTTON MUST SAY WHAT WOULD ENABLE IT.
//
// All three "add a source" buttons in this drawer read "Add 0 rows", "Add 0
// traced items" and "Import 0 elements" while greyed out, with nothing saying
// what to do - so the owner opened the model import, found a dead button and
// a zero, and left (2026-10-04, logs show the panel read and no POST).
//
// A zero is not a reason. The label becomes the ACTION that would enable it,
// and the line underneath says where to perform it, because a refusal has to
// name something a person can actually do.
function Precondition({ children }) {
  return <div style={{ marginTop: 6, fontSize: 12, color: "#8D8D8D", maxWidth: 560 }}>{children}</div>;
}

export function ScheduleTab({ projectId, onDone }) {
  const [rows, setRows] = useState(null);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState({});
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");

  const header = rows ? (rows[headerRow] || []) : [];
  const body = useMemo(
    () => (rows ? rows.slice(headerRow + 1).filter((r) => (r || []).some((c) => String(c || "").trim())) : []),
    [rows, headerRow]
  );

  const observations = useMemo(() => body.slice(0, MAX_ROWS).map((r, i) => {
    const at = (k) => (mapping[k] === undefined ? "" : String(r[mapping[k]] == null ? "" : r[mapping[k]]).trim());
    const len = Number(String(at("length")).replace(/[^\d.]/g, "")) || 0;
    const count = Number(String(at("count")).replace(/[^\d.]/g, "")) || 1;
    return {
      key: `s${i + 1}`,
      mark: at("mark"), name: at("name"), system: at("system"), size: at("size"),
      grid: at("grid"), level: at("level"), length: len, count,
      // One segment covering the run. The field splits it later; a run
      // that arrives un-split is still trackable from minute one.
      segments: [{ key: "s1", label: "whole run", length: len, status: "projected", statusDate: null }],
      raw: r,
    };
  }).filter((o) => o.mark || o.name || o.system), [body, mapping]);

  const usable = observations.length > 0;

  return (
    <div>
      <Steps
        size="small" current={!rows ? 0 : !usable ? 1 : 2}
        items={[{ title: "Choose a file" }, { title: "Map the columns" }, { title: "Add" }]}
        style={{ marginBottom: 16 }}
      />
      <Upload.Dragger
        accept=".xlsx,.xls,.csv"
        maxCount={1}
        showUploadList={false}
        beforeUpload={async (file) => {
          try {
            const data = await readTabular(file);
            if (!data.length) { message.error("That file has no rows"); return false; }
            const h = detectHeaderRow(data);
            setRows(data); setHeaderRow(h); setMapping(guessMapping(data[h] || []));
            setFileName(file.name); setLabel((l) => l || file.name);
          } catch (e) { message.error(e.message || "Could not read that file"); }
          return false;      // parsed here; never uploaded
        }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Drop a take-off here — .xlsx, .xls or .csv</p>
        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
          Read in your browser and mapped below. The file itself is not uploaded.
        </p>
      </Upload.Dragger>

      {rows && (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "14px 0 8px", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#8D8D8D" }}>Header row</span>
            <Select getPopupContainer={popupInStage}
              size="small" style={{ width: 90 }} value={headerRow}
              onChange={(v) => { setHeaderRow(v); setMapping(guessMapping(rows[v] || [])); }}
              options={rows.slice(0, 15).map((_, i) => ({ value: i, label: `Row ${i + 1}` }))}
            />
            <Input
              size="small" style={{ width: 260 }} value={label} placeholder="Name this source"
              onChange={(e) => setLabel(e.target.value)}
            />
            <span style={{ fontSize: 12, color: "#8D8D8D" }}>{body.length} data rows</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8, marginBottom: 12 }}>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <div style={{ fontSize: 11, color: "#3A4952" }}>
                  {f.label} <span style={{ color: "#AEAEAE" }}>{f.hint}</span>
                </div>
                <Select getPopupContainer={popupInStage}
                  size="small" style={{ width: "100%" }} allowClear
                  value={mapping[f.key]}
                  placeholder="not in this file"
                  onChange={(v) => setMapping((m) => ({ ...m, [f.key]: v }))}
                  options={header.map((h, i) => ({ value: i, label: `${String(h || `Column ${i + 1}`)}` }))}
                />
              </div>
            ))}
          </div>

          {!mapping.length && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message="No length column mapped"
              description="Progress will be counted by segment rather than measured by length, and the board will say so. Map a length column if the take-off has one."
            />
          )}

          <Table getPopupContainer={popupInStage}
            size="small" rowKey="key" pagination={false}
            dataSource={observations.slice(0, 5)}
            columns={[
              { title: "Mark", dataIndex: "mark", width: 90 },
              { title: "System", dataIndex: "system", width: 110, render: (v) => v || <span style={{ color: "#AEAEAE" }}>—</span> },
              { title: "Size", dataIndex: "size", width: 80 },
              { title: "Length", dataIndex: "length", width: 70 },
              { title: "Grid", dataIndex: "grid", width: 70 },
              { title: "Level", dataIndex: "level", width: 70 },
            ]}
            locale={{ emptyText: "Map at least a mark, a description or a system" }}
            style={{ marginBottom: 12 }}
          />

          <Button
            type="primary" disabled={!usable} loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await addSource(projectId, { kind: "schedule", label: label || fileName, fileName, observations });
                message.success(`${observations.length} rows queued for matching`);
                onDone();
              } catch (e) { message.error(e.message || "Could not add the source"); }
              finally { setBusy(false); }
            }}
          >
            {usable
              ? `Add ${observations.length} rows as a schedule source`
              : "Map a column to add this schedule"}
          </Button>
          {usable ? null : (
            <Precondition>
              Point at least one column at a mark, a description or a system above. Those are what a
              row is matched on, so a schedule with none of them has nothing to match.
            </Precondition>
          )}
        </>
      )}
    </div>
  );
}

// ---- 2. layout ---------------------------------------------------------
// Tracing, because there is no reliable way to lift a cable tray run off
// a PDF automatically, and a guess drawn confidently on a drawing is
// worse than no line at all. Click along a run, double-click to finish.
export function LayoutTab({ projectId, sheets, onDone }) {
  const [sheetId, setSheetId] = useState(sheets[0] ? String(sheets[0]._id) : "");
  const [img, setImg] = useState("");
  const [loadingImg, setLoadingImg] = useState(false);
  const [draft, setDraft] = useState([]);            // points of the run being traced
  const [traced, setTraced] = useState([]);          // finished runs
  const [system, setSystem] = useState("cableTray");
  const [kind, setKind] = useState("run");
  const [mark, setMark] = useState("");
  const [size, setSize] = useState("");
  const [busy, setBusy] = useState(false);
  const svgRef = useRef(null);

  const sheet = sheets.find((s) => String(s._id) === sheetId);
  useEffect(() => {
    let alive = true;
    setImg(""); setDraft([]);
    if (!sheet || !sheet.link) return undefined;
    // A DWG is traced on its rendered page (sheetOptions: drawable only).
    if (sheetKind(sheet) !== "pdf" && Array.isArray(sheet.pages) && sheet.pages[0]) {
      const u = String(sheet.pages[0]);
      setImg(/^(https?:|data:|blob:)/i.test(u) ? u : `${url}${u.startsWith("/") ? "" : "/"}${u}`);
      return undefined;
    }
    setLoadingImg(true);
    renderPdfThumbnail(sheet.link, { scale: 1.6, maxWidth: 1600 })
      .then((d) => { if (alive) setImg(d || ""); })
      .catch(() => { if (alive) setImg(""); })
      .finally(() => { if (alive) setLoadingImg(false); });
    return () => { alive = false; };
  }, [sheet]);

  // Percent of the sheet image, the same space the overlay reads - and
  // through the SAME stage component, so a run cannot be traced against
  // one box and drawn against another.
  const pointAt = (e) => stagePointAt(svgRef.current, e);

  const finish = () => {
    const need = kind === "gear" ? 1 : 2;
    if (draft.length < need) { message.warning(kind === "gear" ? "Click once to place the gear" : "A run needs at least two points"); return; }
    setTraced((t) => [...t, {
      key: `t${t.length + 1}`, mark: mark.trim(), system, size: size.trim(), kind,
      // Stamped at the moment of measurement, not at upload: this names
      // the build whose maths produced these percentages.
      originVersion: buildVersion(),
      points: draft, sheetDocControlId: sheetId,
      segments: [{ key: "s1", label: mark.trim() || "whole run", length: 0, status: "projected", statusDate: null, points: draft }],
    }]);
    setDraft([]); setMark("");
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 10 }}>
        <Select getPopupContainer={popupInStage} size="small" style={{ minWidth: 260 }} value={sheetId} onChange={setSheetId}
          placeholder="Pick a sheet"
          {...sheetSelectProps(sheets, { labelOf: (s) => `${s.sheetNumber || ""} ${s.sheetName || ""}`.trim() || "Sheet" })} />
        <Select getPopupContainer={popupInStage} size="small" style={{ width: 130 }} value={system} onChange={setSystem}
          options={["cableTray", "conduit", "busDuct", "gear", "duct", "pipe"].map((v) => ({ value: v, label: systemLabel(v) }))} />
        <Select getPopupContainer={popupInStage} size="small" style={{ width: 100 }} value={kind} onChange={setKind}
          options={[{ value: "run", label: "Run" }, { value: "gear", label: "Gear" }]} />
        <Input size="small" style={{ width: 110 }} placeholder="Mark" value={mark} onChange={(e) => setMark(e.target.value)} />
        <Input size="small" style={{ width: 90 }} placeholder="Size" value={size} onChange={(e) => setSize(e.target.value)} />
        <Button size="small" onClick={finish} disabled={!draft.length} type="primary">Finish this one</Button>
        <Button size="small" icon={<UndoOutlined />} onClick={() => setDraft((d) => d.slice(0, -1))} disabled={!draft.length}>Undo point</Button>
      </Space>

      {!sheets.length ? (
        <Empty description="This project has no doc-control sheets to trace on" />
      ) : (
        <div style={{ border: "1px solid #EFEFEF", borderRadius: 6, background: "#fff" }}>
          {loadingImg && <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>}
          {/* Nothing is traceable until the sheet is on screen: without an
              image there is no box to be a percentage OF, and every point
              would be recorded against a placeholder. */}
          {!loadingImg && !img && (
            <div style={{ padding: 60, textAlign: "center", color: "#8D8D8D", fontSize: 12 }}>
              Pick a sheet to trace on.
            </div>
          )}
          {img && (
          <SheetStage
            img={img}
            svgRef={svgRef}
            cursor="crosshair"
            onClick={(e) => {
              const p = pointAt(e);
              if (kind === "gear") { setDraft([p]); return; }
              setDraft((d) => [...d, p]);
            }}
            onDoubleClick={finish}
          >
            {traced.map((t) => (t.kind === "gear"
              ? <circle key={t.key} cx={t.points[0].x} cy={t.points[0].y} r={0.9} fill={systemColor(t.system)} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              : <polyline key={t.key} points={t.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="none"
                          stroke={systemColor(t.system)} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />))}
            {draft.length > 0 && (kind === "gear"
              ? <circle cx={draft[0].x} cy={draft[0].y} r={0.9} fill={systemColor(system)} stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              : <polyline points={draft.map((p) => `${p.x},${p.y}`).join(" ")} fill="none"
                          stroke={systemColor(system)} strokeWidth={3} strokeDasharray="6 4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />)}
            {draft.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={0.6} fill="#fff" stroke={systemColor(system)} strokeWidth={2} vectorEffect="non-scaling-stroke" />)}
          </SheetStage>
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#8D8D8D" }}>
          Click along a run · double-click to finish it · {traced.length} traced
        </span>
        <span style={{ flex: 1 }} />
        {traced.map((t) => (
          <Tag key={t.key} closable onClose={() => setTraced((x) => x.filter((y) => y.key !== t.key))}
               color={systemColor(t.system)}>
            {t.mark || systemLabel(t.system)}
          </Tag>
        ))}
      </div>

      <Button
        type="primary" style={{ marginTop: 12 }} disabled={!traced.length} loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await addSource(projectId, {
              kind: "layout",
              label: `${sheet?.sheetNumber || "Sheet"} traced ${moment().format("MMM D")}`,
              sheetDocControlId: sheetId,
              observations: traced,
            });
            message.success(`${traced.length} traced runs queued for matching`);
            onDone();
          } catch (e) { message.error(e.message || "Could not add the source"); }
          finally { setBusy(false); }
        }}
      >
        {traced.length
          ? <>Add {traced.length} traced item{traced.length === 1 ? "" : "s"} as a layout source</>
          : "Trace an item to add it"}
      </Button>
      {traced.length ? null : (
        <Precondition>
          Click the points of an item on the sheet above and finish it; each one you finish becomes a
          tag here. Nothing has been traced yet, so there is nothing to add.
        </Precondition>
      )}
    </div>
  );
}

// ---- 3. model ----------------------------------------------------------
export function ModelTab({ projectId, onDone }) {
  const [data, setData] = useState(null);
  const [commitId, setCommitId] = useState("");
  const [picked, setPicked] = useState([]);
  const [levels, setLevels] = useState(null);          // { total, levels, hasLevels } once asked for
  const [levelsBusy, setLevelsBusy] = useState(false);
  const [pickedLevels, setPickedLevels] = useState([]);
  const [busy, setBusy] = useState(false);
  // THE TICKS ARE REMEMBERED (owner, 2026-09-27: "the source should
  // remember the ticked categories so i do not have to tick them every
  // time"). Per person and per project, like every other remembered
  // state on this page.
  const [remembered, setRemembered] = usePreference("tracking.sourceCategories", [], { projectId: projectId || "" });
  const [missingTicks, setMissingTicks] = useState([]);
  // Applied once per commit. The candidates and the stored preference
  // arrive from two different requests in either order, so neither one
  // can be the trigger on its own - this says whether THIS commit has
  // had its ticks restored yet, and the effect below fires whenever the
  // second of the two lands.
  const appliedFor = useRef("");

  const fetchCandidates = (cid) => {
    fetch(`${url}/projects/${projectId}/field-tracking/sources/model-candidates${cid ? `?commitId=${cid}` : ""}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json())
      .then((d) => {
        appliedFor.current = "";      // a different commit restores again
        setData(d); setCommitId(d.commitId || ""); setPicked([]); setMissingTicks([]);
      })
      .catch(() => setData({ commits: [], categories: [] }));
  };
  useEffect(() => { fetchCandidates(); /* eslint-disable-next-line */ }, [projectId]);

  // Restore the remembered ticks against THIS commit's categories, once.
  useEffect(() => {
    if (!data || !commitId || appliedFor.current === commitId) return;
    if (!(remembered || []).length) return;
    const { picked: keep, missing } = restoreTicks(remembered, data.categories);
    appliedFor.current = commitId;
    setPicked(keep);
    setMissingTicks(missing);
  }, [data, commitId, remembered]);

  if (!data) return <div style={{ padding: 40, textAlign: "center" }}><Spin /></div>;
  if (!data.commits.length) {
    return <Empty description="No model commits on this project yet — the Revit / Navisworks plugin has not pushed one." />;
  }
  const total = data.categories.filter((c) => picked.includes(c.category)).reduce((n, c) => n + c.count, 0);
  // THE LEVELS THIS COMMIT ACTUALLY HAS (owner, 2026-09-20: "allow any
  // category, but force a level or zone"). The refusal shipped telling
  // people to pick a level while nothing ever asked for one - a remedy
  // that existed in neither the UI nor the API. They are read from the
  // commit, never typed, because a level the model does not contain
  // makes an empty source and explains nothing.
  const post = async (path, body) => {
    const r = await fetch(`${url}/projects/${projectId}/field-tracking/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) { const e = new Error(d.err || "Failed"); e.body = d; throw e; }
    return d;
  };
  const loadLevels = async () => {
    setLevelsBusy(true);
    try {
      const d = await post("sources/model-levels", { commitId, categories: picked });
      setLevels(d);
      if (!d.hasLevels) message.info("These elements carry no level, so this commit cannot be split by level.");
    } catch (e) { message.error(e.message || "Could not read the levels"); }
    finally { setLevelsBusy(false); }
  };
  const scopedTotal = (levels && pickedLevels.length)
    ? (levels.levels || []).filter((l) => pickedLevels.includes(l.label)).reduce((n, l) => n + l.n, 0)
    : total;

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="The model brings what a thing IS, not where it is on a sheet"
        description="Elements arrive with their category, size and level. They get a position on the drawing when they are matched to a traced or scheduled item that already has one — until the plugin exports sheet viewports, the model cannot place itself."
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: "#8D8D8D" }}>Commit</span>
        <Select getPopupContainer={popupInStage}
          size="small" style={{ minWidth: 320 }} value={commitId}
          onChange={(v) => { setData(null); fetchCandidates(v); }}
          options={data.commits.map((c) => ({
            value: String(c._id),
            label: `${moment(c.ts).format("MMM D, HH:mm")} · ${c.revitDocTitle || "model"}`,
          }))}
        />
      </Space>
      {missingTickNote(missingTicks) ? (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Some remembered ticks are not in this commit"
          description={missingTickNote(missingTicks)}
        />
      ) : null}
      <Table getPopupContainer={popupInStage}
        size="small" rowKey="category" pagination={false}
        dataSource={data.categories}
        rowSelection={{
          selectedRowKeys: picked,
          // The levels belong to the CATEGORIES they were read for:
          // change the pick and they are different levels with different
          // counts, so they are asked for again rather than left to
          // stand for a set nobody chose.
          onChange: (keys) => {
            setPicked(keys); setLevels(null); setPickedLevels([]);
            // A DELIBERATE TICK CLOSES THE RESTORE. The stored
            // preference can still be in flight when somebody ticks a
            // box, and a restore landing after that would overwrite a
            // choice they just made with one they made yesterday.
            appliedFor.current = commitId;
            // Saved on every change, so unticking everything is
            // remembered as "nothing" rather than restoring the old set
            // over the top of a deliberate clear.
            setRemembered(keys.map(String));
            setMissingTicks([]);
          },
        }}
        columns={[
          { title: "Revit category", dataIndex: "category" },
          {
            title: "Tracks as", dataIndex: "system", width: 140,
            render: (v) => (v
              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: systemColor(v) }} />{systemLabel(v)}
                </span>
              : <span style={{ color: "#AEAEAE" }}>its own name</span>),
          },
          { title: "Elements", dataIndex: "count", width: 90, align: "right" },
        ]}
        scroll={{ y: 300 }}
      />
      {/* A SOURCE PER LEVEL, which is what he chose over a size limit.
          Offered before anything is refused rather than after, so it is a
          way of working and not an apology. */}
      {picked.length ? (
        <div style={{ marginTop: 12, padding: "8px 10px", border: "1px solid #EFEFEF", borderRadius: 6, background: "#FAFBFC" }}>
          <Space wrap>
            <b style={{ fontSize: 12 }}>Level</b>
            {levels ? null : (
              <Button size="small" loading={levelsBusy} onClick={loadLevels}>
                Split by level
              </Button>
            )}
            {levels && !levels.hasLevels ? (
              <span style={{ fontSize: 12, color: "#8D8D8D" }}>
                these elements carry no level, so this commit cannot be split by one
              </span>
            ) : null}
            {levels && levels.hasLevels ? (
              <>
                <Select getPopupContainer={popupInStage}
                  size="small" mode="multiple" allowClear style={{ minWidth: 320 }}
                  placeholder="Every level"
                  value={pickedLevels}
                  onChange={setPickedLevels}
                  options={(levels.levels || []).map((l) => ({
                    value: l.label,
                    label: `${l.label} · ${l.n.toLocaleString()}${l.n > levels.limit ? " (still too many)" : ""}`,
                  }))}
                />
                <span style={{ fontSize: 12, color: "#8D8D8D" }}>
                  {pickedLevels.length
                    ? `${scopedTotal.toLocaleString()} of ${total.toLocaleString()}`
                    : `${(levels.levels || []).length} level${(levels.levels || []).length === 1 ? "" : "s"} in these categories`}
                </span>
              </>
            ) : null}
          </Space>
        </div>
      ) : null}
      <Button
        type="primary" style={{ marginTop: 12 }} disabled={!picked.length} loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const d = await post("sources/from-model", {
              commitId, categories: picked,
              ...(pickedLevels.length ? { levels: pickedLevels } : {}),
            });
            message.success(`${d.count} elements queued for matching`);
            onDone();
          } catch (e) {
            message.error(e.message || "Could not import");
            // A REFUSAL THAT NAMES A LEVEL NOW HANDS THE LEVELS OVER, so
            // the next thing a person sees is the list it told them to
            // pick from rather than the same sentence again.
            if (e.body && Array.isArray(e.body.levels)) setLevels({ ...e.body, total: e.body.count });
          }
          finally { setBusy(false); }
        }}
      >
        {picked.length
          ? <>
              Import {scopedTotal.toLocaleString()} element{scopedTotal === 1 ? "" : "s"} as a model source
              {pickedLevels.length ? ` · ${pickedLevels.join(", ")}` : ""}
            </>
          : "Tick a category above to import"}
      </Button>
      {picked.length ? null : (
        <Precondition>
          The table lists every category in this model version with the number of elements it holds.
          Tick the ones you want tracked and this imports them as one source; tick nothing and there is
          nothing to import.
        </Precondition>
      )}
    </div>
  );
}

// ---- the drawer --------------------------------------------------------
const STATE_TAG = {
  pending: { color: "default", text: "queued" },
  matched: { color: "blue", text: "waiting on review" },
  applied: { color: "green", text: "applied" },
  failed: { color: "red", text: "failed" },
};

export default function TrackingSources({
  projectId, sheets, onChanged,
  // WHICH IMPORTS ARE BEING APPLIED RIGHT NOW, and which last failed -
  // from the board, by groupId (owner, 2026-10-02: "just mark it if its
  // being processed, and show a ui, saying next to it its being
  // processed"). Taken as props because the page has already asked the
  // server for them; a second request here would be a second answer.
  processing = [],
  applyFailures = [],
  // CRUD for this caller (board.perms, 2026-10-14): no Add key, no import
  // tabs; no Delete key, no remove button. Absent = as before.
  canAdd = true,
  canDelete = true,
}) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  // GROUPED BY THE MESSAGE, not listed per row: sixteen parts failing for
  // one reason is one fact, and sixteen copies of it is a wall.
  const failed = (sources || []).filter((s) => s.status === "failed");
  const failedCount = failed.length;
  const failedSummary = Object.entries(
    failed.reduce((acc, s) => {
      const t = (s.error || "").trim() || "No reason was recorded.";
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]).map(([text, count]) => ({ text, count }));

  const reload = async () => {
    setLoading(true);
    try { const r = await getSources(projectId); setSources(r.sources || []); }
    catch (e) { message.error(e.message || "Could not list the sources"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  // Not `onChanged?.()` as a bare statement: the build's eslint does not
  // recognise an optional CALL as a call and fails it as an unused
  // expression. Every other use in the codebase happens to be awaited,
  // which is why they pass.
  const done = async () => { await reload(); if (onChanged) onChanged(); };

  return (
    <div>
      {!canAdd ? (
        <div style={{ fontSize: 12, color: "#8D8D8D", marginBottom: 8 }}>
          Adding a source needs the Construction tracking Add permission.
        </div>
      ) : null}
      <Tabs
        style={canAdd ? undefined : { display: "none" }}
        items={[
          { key: "schedule", label: <span><FileExcelOutlined /> Schedule</span>,
            children: <ScheduleTab projectId={projectId} onDone={done} /> },
          { key: "layout", label: <span><HighlightOutlined /> Layout</span>,
            children: <LayoutTab projectId={projectId} sheets={sheets} onDone={done} /> },
          { key: "model", label: <span><BuildOutlined /> Model</span>,
            children: <ModelTab projectId={projectId} onDone={done} /> },
        ]}
      />

      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em", marginBottom: 8 }}>
          SOURCES ON THIS PROJECT
        </div>

        {/* A FAILED PART NEVER PROGRESSES, AND THE COUNT DOES NOT SAY SO.
            Owner, 2026-10-02: "source, import we have 8 or 12 queued jobs
            and the progress is not fast enough". Sixteen of those parts had
            been FAILED since 28 September - all on the 16 MB document
            ceiling - so the number he was watching could never fall. The
            State column did say "failed", in a tag, with the reason hidden
            in a HOVER and unreadable when found ("The value of offset is out
            of range... Received 17825794").
            So: say it above the table, in the words the server now gives,
            and group identical failures rather than printing sixteen rows of
            the same sentence. */}
        {failedSummary.length ? (
          <Alert
            type="warning" showIcon style={{ marginBottom: 10 }}
            message={`${failedCount} source part${failedCount === 1 ? "" : "s"} failed and will not progress on their own`}
            description={(
              <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {failedSummary.map((g) => (
                  <div key={g.text} style={{ marginBottom: 4 }}>
                    <b>{g.count}×</b> {g.text}
                  </div>
                ))}
                <div style={{ color: "#8D8D8D", marginTop: 6 }}>
                  They are counted in the list below but will never move. Remove them, or import the
                  selection again - a fresh import is split into smaller parts.
                </div>
              </div>
            )}
          />
        ) : null}
        <Table getPopupContainer={popupInStage}
          size="small" rowKey="_id" loading={loading} dataSource={sources} pagination={false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sources yet" /> }}
          columns={[
            { title: "Source", key: "label", render: (_, r) => (
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.label || r.fileName || r.kind}</div>
                <div style={{ fontSize: 10.5, color: "#8D8D8D" }}>
                  {r.kind} · {r.observationCount} rows · {moment(r.createdAt).format("MMM D, HH:mm")}
                  {r.uploadedByName ? ` · ${r.uploadedByName}` : ""}
                </div>
              </div>
            ) },
            { title: "State", dataIndex: "status", width: 210, render: (v, r) => {
              // BEING PROCESSED BEATS THE STORED STATUS, because the status
              // is what the source IS and this is what is happening to it.
              // A part mid-apply still reads `matched` on disk for the
              // whole six to eight minutes, which is exactly the row the
              // owner read as un-actioned.
              const live = r.groupId ? processing.find((x) => String(x.groupId) === String(r.groupId)) : null;
              const bad = !live && r.groupId
                ? applyFailures.find((x) => String(x.groupId) === String(r.groupId)) : null;
              return (
                <Tooltip getPopupContainer={popupInStage} title={
                  live
                    ? `Applying this import${live.partsInGroup ? `, part ${Math.min(live.partsDone + 1, live.partsInGroup)} of ${live.partsInGroup}` : ""}${live.mine ? "" : ` - started by ${live.by || "somebody else"}`}. It runs on the worker and takes a few minutes.`
                    : bad
                      ? `${bad.error || "The apply stopped without finishing."} Please reach out to the person who exported the model.`
                      : (r.seeded ? "First source on the project — it seeded the board, so there was nothing to review" : (r.error || ""))
                }>
                  {live ? (
                    <Tag color="processing" icon={<Spin size="small" style={{ marginRight: 4 }} />}>being processed</Tag>
                  ) : (
                    <Tag color={(STATE_TAG[v] || {}).color}>{(STATE_TAG[v] || {}).text || v}</Tag>
                  )}
                  {bad ? <Tag color="error">apply failed</Tag> : null}
                  {r.seeded ? <Tag>seeded</Tag> : null}
                  {/* WHERE IT IS IN THE LINE (owner, 2026-10-02: "show the
                      queue position"). The server composes the sentence -
                      counts only, never whose the other jobs are, because
                      this queue is shared across projects. A part with no
                      job AND still pending reads "not queued", which is
                      what nine orphan parts looked like tonight with
                      nothing on screen saying so. */}
                  {!live && r.queueNote ? (
                    <div style={{
                      fontSize: 11, marginTop: 2, lineHeight: 1.35,
                      color: /not queued|not moving|busy/.test(r.queueNote) ? "#A8071A" : "#8D8D8D",
                    }}>
                      {r.queueNote}
                    </div>
                  ) : null}
                </Tooltip>
              );
            } },
            { title: "", width: 44, key: "x", render: (_, r) => (!canDelete ? null : (
              <Popconfirm getPopupContainer={popupInStage}
                title="Remove this source?"
                description="Its proposals are discarded. Items already applied to the board stay."
                onConfirm={async () => {
                  try { await deleteSource(projectId, r._id); await done(); }
                  catch (e) { message.error(e.message || "Could not delete"); }
                }}
              >
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )) },
          ]}
        />
      </div>
    </div>
  );
}
